import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Plus, Send, Eye, Edit3, CheckCircle, ShieldAlert, XCircle, X, Bell, BellRing } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog, promptDialog } from '../../lib/confirmStore';
import FormBuilder from '../shared/FormBuilder';
import ChoicesEditor from '../shared/ChoicesEditor';
import { renderFormattedResponseValue } from '../../lib/responseFormatter';
import type { OrgSpecialRequest, OrgSpecialRequestTemplate } from '../../types';
import { fetchOrgSpecialRequestTemplates } from '../../lib/orgSpecialRequestTemplates';

/**
 * OrgSpecialRequestsSection — Special Requests issued to a Referrer,
 * Sponsor, or Employer, shown inside the org's admin detail view.
 * Backed by /api/admin/org-special-requests (same documentRequests
 * system + token completion flow as applicant Special Requests).
 * A Submitted request is completed only by an admin review action;
 * overrides/cancellations require a recorded reason. Verification
 * requests never show override controls — they complete only on a
 * Didit-confirmed approval.
 */

const RESPONSE_TYPES: { value: string; label: string }[] = [
  { value: 'short_text', label: 'Short Text Response' },
  { value: 'long_text', label: 'Long Text Response' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'form', label: 'Form' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
];

/** Client copy of the server's choices rule (orgSpecialRequestsCore). */
const CHOICES_REQUIRED_MESSAGE =
  'A Dropdown Choice or Multiple Choice request requires at least one choice. Add the choices the organization can pick from, or choose a different response type.';

/** Stored choices are plain strings; tolerate {label}/{value} objects on read. */
function toChoiceList(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((o: any) => String(typeof o === 'string' ? o : o?.label || o?.value || ''));
}

const STATUS_STYLES: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600',
  Pending: 'bg-amber-100 text-amber-700',
  Viewed: 'bg-sky-100 text-sky-700',
  Submitted: 'bg-indigo-100 text-indigo-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-red-100 text-red-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Cancelled: 'bg-red-100 text-red-600',
  Overdue: 'bg-red-100 text-red-700',
  Expired: 'bg-red-100 text-red-700',
};

const TERMINAL = ['Completed', 'Cancelled', 'Approved'];

function fmtDate(v?: string | null) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

interface FormState {
  title: string;
  responseType: string;
  description: string;
  instructions: string;
  requiredDocs: string;
  dueDate: string;
  priority: string;
  mandatory: boolean;
  requiresApproval: boolean;
  templateId: string;
  templateName: string;
  formFields: any[];
  dropdownOptions: string[];
  notifyAdminOnSubmit: boolean;
  notifyAdminOnCompletion: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  responseType: 'file_upload',
  description: '',
  instructions: '',
  requiredDocs: '',
  dueDate: '',
  priority: 'Medium',
  mandatory: false,
  requiresApproval: true,
  templateId: '',
  templateName: '',
  formFields: [],
  dropdownOptions: [],
  notifyAdminOnSubmit: false,
  notifyAdminOnCompletion: true,
};

export default function OrgSpecialRequestsSection({
  orgType,
  orgId,
  canEdit = true,
}: {
  orgType: 'referrer' | 'employer' | 'sponsor';
  orgId: string;
  canEdit?: boolean;
}) {
  const [requests, setRequests] = useState<OrgSpecialRequest[]>([]);
  const [outstandingCount, setOutstandingCount] = useState(0);
  const [recipient, setRecipient] = useState<{ userId: string; email: string; name: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState<OrgSpecialRequest | null>(null);
  const [requestType, setRequestType] = useState<'Standard' | 'Verification' | 'Form'>('Standard');
  const [workflows, setWorkflows] = useState<{ id: string; label: string }[]>([]);
  const [workflowId, setWorkflowId] = useState('');
  const [templates, setTemplates] = useState<OrgSpecialRequestTemplate[]>([]);

  // Org templates targeting THIS org's type only (dedicated org store).
  useEffect(() => {
    fetchOrgSpecialRequestTemplates({ orgType, activeOnly: true })
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [orgType]);

  const applyTemplate = (templateId: string) => {
    const t = templates.find((x) => x.templateId === templateId);
    if (!t) {
      set({ templateId: '', templateName: '' });
      return;
    }
    if (t.requestType === 'Form') setRequestType('Form');
    setForm((f) => ({
      ...f,
      templateId: t.templateId,
      templateName: t.templateName,
      title: t.title || f.title,
      description: t.description || f.description,
      instructions: t.instructions || f.instructions,
      responseType: t.responseType || f.responseType,
      priority: t.priority || f.priority,
      requiredDocs: (t.requiredDocs || []).map((d: any) => (typeof d === 'string' ? d : d?.name || '')).filter(Boolean).join('\n'),
      formFields: Array.isArray(t.formFields) ? t.formFields : [],
      dropdownOptions: toChoiceList(t.dropdownOptions),
      mandatory: t.blocksWorkspaceAccess === true,
      requiresApproval: t.requiresApproval !== false,
      notifyAdminOnSubmit: t.notifyAdminOnSubmit === true,
      notifyAdminOnCompletion: t.notifyAdminOnCompletion !== false,
      dueDate: t.defaultDueDays != null
        ? new Date(Date.now() + t.defaultDueDays * 86400000).toISOString().slice(0, 10)
        : f.dueDate,
    }));
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await safeFetch(`/api/admin/orgs/${orgType}/${orgId}/special-requests`);
      setRequests(data.requests || []);
      setOutstandingCount(data.outstandingCount || 0);
      setRecipient(data.recipient || null);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to load special requests');
    } finally {
      setLoading(false);
    }
  }, [orgType, orgId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setRequestType('Standard');
    setShowForm(true);
  };

  const pickRequestType = async (t: 'Standard' | 'Verification' | 'Form') => {
    setRequestType(t);
    if (t === 'Verification' && workflows.length === 0) {
      try {
        const data = await safeFetch('/api/admin/didit/workflows');
        setWorkflows(data.workflows || []);
        if ((data.workflows || []).length > 0) setWorkflowId(data.workflows[0].id);
      } catch {
        setWorkflows([]);
      }
    }
  };

  const openEdit = (r: OrgSpecialRequest) => {
    setEditingId(r.requestId);
    setForm({
      title: r.documentName || '',
      responseType: r.responseType || 'file_upload',
      description: r.description || '',
      instructions: r.instructions || '',
      requiredDocs: (r.requiredDocs || []).map((d: any) => (typeof d === 'string' ? d : d?.name || '')).filter(Boolean).join('\n'),
      dueDate: r.dueDate ? String(r.dueDate).slice(0, 10) : '',
      priority: r.priority || 'Medium',
      mandatory: (typeof r.blocksWorkspaceAccess === 'boolean' ? r.blocksWorkspaceAccess : r.mandatory) === true,
      requiresApproval: r.requiresApproval !== false,
      templateId: r.templateId || '',
      templateName: r.templateName || '',
      formFields: Array.isArray(r.formFields) ? r.formFields : [],
      dropdownOptions: toChoiceList(r.dropdownOptions),
      notifyAdminOnSubmit: r.notifyAdminOnSubmit === true,
      notifyAdminOnCompletion: r.notifyAdminOnCompletion === true,
    });
    setShowForm(true);
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim(),
    instructions: form.instructions,
    requestType: editingId ? undefined : requestType,
    responseType: requestType === 'Form' && !editingId ? 'form' : form.responseType,
    priority: form.priority,
    dueDate: form.dueDate ? new Date(form.dueDate + 'T23:59:59').toISOString() : null,
    requiredDocs: form.requiredDocs.split('\n').map((s) => s.trim()).filter(Boolean),
    formFields: form.formFields,
    dropdownOptions: form.dropdownOptions.map((s) => s.trim()).filter(Boolean),
    mandatory: form.mandatory,
    blocksWorkspaceAccess: form.mandatory,
    requiresApproval: form.requiresApproval,
    templateId: form.templateId || undefined,
    templateName: form.templateName || undefined,
    notifyAdminOnSubmit: form.notifyAdminOnSubmit,
    notifyAdminOnCompletion: form.notifyAdminOnCompletion,
  });

  const handleSave = async (send: boolean) => {
    if (!form.title.trim() || !form.description.trim()) {
      notifyToast('error', 'A title and description are required');
      return;
    }
    if (send && !form.dueDate && requestType !== 'Verification') {
      notifyToast('error', 'A due date is required to send the request');
      return;
    }
    if (requestType === 'Form' && !editingId && form.formFields.length === 0) {
      notifyToast('error', 'A Form request requires at least one form field. Add a field in the form builder, or choose the Standard request type.');
      return;
    }
    // Choice-based response types need at least one choice — the
    // same rule the server core enforces (ORG_CHOICES_REQUIRED_ERROR).
    const effectiveResponseType = requestType === 'Form' && !editingId ? 'form' : form.responseType;
    if (
      requestType !== 'Verification' &&
      (effectiveResponseType === 'dropdown' || effectiveResponseType === 'multiple_choice') &&
      form.dropdownOptions.map((s) => s.trim()).filter(Boolean).length === 0
    ) {
      notifyToast('error', CHOICES_REQUIRED_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      if (!editingId && requestType === 'Verification') {
        await safeFetch('/api/admin/org-verification-requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orgType, orgId, send,
            title: form.title.trim(),
            description: form.description.trim(),
            instructions: form.instructions,
            dueDate: form.dueDate ? new Date(form.dueDate + 'T23:59:59').toISOString() : null,
            priority: form.priority,
            mandatory: form.mandatory,
            notifyAdminOnSubmit: form.notifyAdminOnSubmit,
            notifyAdminOnCompletion: form.notifyAdminOnCompletion,
            workflowId: workflowId || undefined,
          }),
        });
        notifyToast('success', send ? 'Verification request sent' : 'Verification request saved as draft');
        setShowForm(false);
        await load();
        return;
      }
      if (editingId) {
        await safeFetch(`/api/admin/org-special-requests/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload()),
        });
        if (send) {
          await safeFetch(`/api/admin/org-special-requests/${editingId}/send`, { method: 'POST' });
        }
        notifyToast('success', send ? 'Request updated and sent' : 'Request updated');
      } else {
        await safeFetch('/api/admin/org-special-requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgType, orgId, send, ...buildPayload() }),
        });
        notifyToast('success', send ? 'Request created and sent' : 'Request saved as draft');
      }
      setShowForm(false);
      setEditingId(null);
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save request');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (r: OrgSpecialRequest) => {
    if (!(await confirmDialog(`Send the special request "${r.documentName}" to this organization? The organization will be notified and can complete it right away.`, { confirmLabel: 'Send request' }))) return;
    try {
      await safeFetch(`/api/admin/org-special-requests/${r.requestId}/send`, { method: 'POST' });
      notifyToast('success', 'Request sent');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to send request');
    }
  };

  const handleApprove = async (r: OrgSpecialRequest) => {
    if (!(await confirmDialog(`Approve the submission for "${r.documentName}"? The organization will be notified and the request will leave the outstanding list.`))) return;
    try {
      await safeFetch(`/api/admin/org-special-requests/${r.requestId}/approve`, { method: 'POST' });
      notifyToast('success', 'Submission approved');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to approve the submission');
    }
  };

  const handleReject = async (r: OrgSpecialRequest) => {
    const reason = await promptDialog(`Reject the submission for "${r.documentName}"? The reason is required — the organization sees it and resubmits against it.`);
    if (reason === null) return;
    if (reason.trim().length < 3) {
      notifyToast('error', 'A rejection requires a recorded reason');
      return;
    }
    try {
      await safeFetch(`/api/admin/org-special-requests/${r.requestId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      notifyToast('success', 'Submission rejected — the organization has been notified with the reason');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to reject the submission');
    }
  };

  const handleComplete = async (r: OrgSpecialRequest) => {
    if (!(await confirmDialog(`Mark "${r.documentName}" as Completed after your review?`))) return;
    try {
      await safeFetch(`/api/admin/org-special-requests/${r.requestId}/complete`, { method: 'POST' });
      notifyToast('success', 'Request completed');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to complete request');
    }
  };

  const handleOverride = async (r: OrgSpecialRequest) => {
    const reason = await promptDialog(`Override "${r.documentName}" and mark it Completed without a submission? A reason is required and will be recorded.`);
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) notifyToast('error', 'An override requires a recorded reason');
      return;
    }
    try {
      await safeFetch(`/api/admin/org-special-requests/${r.requestId}/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      notifyToast('success', 'Request overridden');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to override request');
    }
  };

  const handleCancel = async (r: OrgSpecialRequest) => {
    const reason = await promptDialog(`Cancel "${r.documentName}"? A reason is required and will be recorded.`);
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) notifyToast('error', 'A cancellation requires a recorded reason');
      return;
    }
    try {
      await safeFetch(`/api/admin/org-special-requests/${r.requestId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      notifyToast('success', 'Request cancelled');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to cancel request');
    }
  };

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div className="border border-slate-200 rounded-2xl p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
          <ClipboardList className="h-3.5 w-3.5" /> Special Requests
        </h3>
        <div className="flex items-center gap-2">
          {outstandingCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-red-100 text-red-700">
              <ShieldAlert className="h-3 w-3" /> {outstandingCount} mandatory outstanding
            </span>
          )}
          {canEdit && (
            <button onClick={openCreate} className="flex items-center gap-1 px-2.5 py-1.5 btn-primary text-white rounded-lg text-[11px] font-bold cursor-pointer">
              <Plus className="h-3 w-3" /> Create Request
            </button>
          )}
        </div>
      </div>

      {recipient && (
        <p className="text-[11px] text-slate-500 mb-3">
          Requests are delivered to the designated main user: {recipient.name || '—'}{recipient.email ? ` (${recipient.email})` : ''}
        </p>
      )}

      {showForm && canEdit && (
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
          {!editingId && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Type</span>
              {(['Standard', 'Form', 'Verification'] as const).map((t) => (
                <button key={t} onClick={() => pickRequestType(t)} className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer ${requestType === t ? 'btn-primary text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>
                  {t}
                </button>
              ))}
              {requestType === 'Verification' && (
                <span className="text-[11px] text-slate-500">Completes only when Didit confirms the main user&apos;s verification. It cannot be overridden.</span>
              )}
              {requestType === 'Form' && (
                <span className="text-[11px] text-slate-500">The organization completes a form you build below — the same form experience as applicant special requests.</span>
              )}
            </div>
          )}
          {!editingId && requestType !== 'Verification' && templates.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Template</span>
              <select
                value={form.templateId}
                onChange={(e) => applyTemplate(e.target.value)}
                className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
              >
                <option value="">Start from scratch (ad hoc)</option>
                {templates.map((t) => (
                  <option key={t.templateId} value={t.templateId}>{t.templateName}</option>
                ))}
              </select>
              {form.templateId && <span className="text-[11px] text-slate-500">Fields prefilled from the template — adjust before sending if needed.</span>}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Request title" className="px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white" />
            {requestType === 'Verification' && !editingId ? (
              <select value={workflowId} onChange={(e) => setWorkflowId(e.target.value)} className="px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white">
                {workflows.length === 0 && <option value="">No Didit workflow configured</option>}
                {workflows.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
              </select>
            ) : requestType === 'Form' && !editingId ? (
              <div className="px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-slate-100 text-slate-500 flex items-center">
                Response format: Form (fields + files)
              </div>
            ) : (
              <select value={form.responseType} onChange={(e) => set({ responseType: e.target.value })} className="px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white">
                {RESPONSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            )}
          </div>
          {(requestType === 'Form' || (editingId && form.responseType === 'form')) && requestType !== 'Verification' && (
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Form fields</p>
              <FormBuilder
                fields={form.formFields as any}
                onChange={(fields) => set({ formFields: fields })}
              />
              {form.formFields.length === 0 && (
                <p className="text-[11px] text-red-500 mt-1">A Form request requires at least one form field.</p>
              )}
            </div>
          )}
          {requestType !== 'Verification' && requestType !== 'Form' && (form.responseType === 'dropdown' || form.responseType === 'multiple_choice') && (
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Choices</p>
              <ChoicesEditor
                options={form.dropdownOptions}
                onChange={(options) => set({ dropdownOptions: options })}
              />
              {form.dropdownOptions.map((s) => s.trim()).filter(Boolean).length === 0 && (
                <p className="text-[11px] text-red-500 mt-1">{CHOICES_REQUIRED_MESSAGE}</p>
              )}
            </div>
          )}
          <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Description and instructions for the recipient" rows={3} className="w-full px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white" />
          <textarea value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} placeholder="Additional instructions (optional)" rows={2} className="w-full px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white" />
          {requestType === 'Standard' && (
            <textarea value={form.requiredDocs} onChange={(e) => set({ requiredDocs: e.target.value })} placeholder="Required information or documents (one per line)" rows={2} className="w-full px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white" />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="flex items-center gap-2 text-[11px] text-slate-500">
              Due date
              <input type="date" value={form.dueDate} onChange={(e) => set({ dueDate: e.target.value })} className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" />
            </label>
            <label className="flex items-center gap-2 text-[11px] text-slate-500">
              Priority
              <select value={form.priority} onChange={(e) => set({ priority: e.target.value })} className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white">
                <option>Low</option><option>Medium</option><option>High</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 font-semibold">
              <input type="checkbox" checked={form.mandatory} onChange={(e) => set({ mandatory: e.target.checked })} />
              Blocks workspace access while outstanding
            </label>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <input type="checkbox" checked={form.requiresApproval} onChange={(e) => set({ requiresApproval: e.target.checked })} />
              Requires admin approval after submission
            </label>
            {form.templateName && (
              <span className="text-[11px] text-slate-500">From template: {form.templateName}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <input type="checkbox" checked={form.notifyAdminOnSubmit} onChange={(e) => set({ notifyAdminOnSubmit: e.target.checked })} />
              <Bell className="h-3 w-3" /> Notify me when submitted
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <input type="checkbox" checked={form.notifyAdminOnCompletion} onChange={(e) => set({ notifyAdminOnCompletion: e.target.checked })} />
              <BellRing className="h-3 w-3" /> Notify me when completed
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="px-3 py-1.5 text-[11px] font-bold text-slate-500 cursor-pointer">Close</button>
            <button onClick={() => handleSave(false)} disabled={saving} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-[11px] font-bold cursor-pointer disabled:opacity-50">
              {editingId ? 'Save Changes' : 'Save Draft'}
            </button>
            <button onClick={() => handleSave(true)} disabled={saving} className="px-3 py-1.5 btn-primary text-white rounded-lg text-[11px] font-bold cursor-pointer disabled:opacity-50">
              {saving ? 'Saving…' : editingId ? 'Save & Send' : 'Create & Send'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-slate-500 py-2">Loading requests…</p>
      ) : requests.length === 0 ? (
        <p className="text-xs text-slate-500 py-2">No special requests issued to this organization yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {requests.map((r) => (
            <li key={r.requestId} className="py-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-700 truncate">
                    {r.documentName}
                    {r.requestType === 'Verification' && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold">Verification</span>}
                    {r.requestType === 'Form' && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold">Form</span>}
                    {r.mandatory && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-bold">Mandatory</span>}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    To {r.recipientEmail || '—'}
                    {r.sentAt ? ` · issued ${fmtDate(r.sentAt)}` : ` · drafted ${fmtDate(r.createdAt)}`}
                    {r.dueDate ? ` · due ${fmtDate(r.dueDate)}` : ''}
                    {r.submittedAt ? ` · submitted ${fmtDate(r.submittedAt)}` : ''}
                    {r.completedAt ? ` · completed ${fmtDate(r.completedAt)}` : ''}
                  </p>
                  {r.requestType === 'Verification' && r.verificationStatus && (
                    <p className="text-[11px] text-violet-500">
                      Didit: {r.verificationStatus}{r.diditSessionId ? ` · ref ${r.diditSessionId}` : ''}
                      {r.verificationIssue ? ` — ${r.verificationIssue}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${STATUS_STYLES[r.status] || 'bg-slate-100 text-slate-500'}`}>
                    {r.requestType === 'Verification' && !TERMINAL.includes(r.status) ? 'Pending Verification' : r.status}
                  </span>
                  <button onClick={() => setViewing(r)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 cursor-pointer" title="View Request"><Eye className="h-3.5 w-3.5" /></button>
                  {canEdit && r.status === 'Draft' && (
                    <button onClick={() => handleSend(r)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer" title="Send"><Send className="h-3.5 w-3.5" /></button>
                  )}
                  {canEdit && ['Draft', 'Pending', 'Viewed'].includes(r.status) && r.requestType !== 'Verification' && (
                    <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer" title="Edit"><Edit3 className="h-3.5 w-3.5" /></button>
                  )}
                  {canEdit && r.status === 'Submitted' && r.requestType !== 'Verification' && r.requiresApproval !== false && (
                    <>
                      <button onClick={() => handleApprove(r)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer" title="Approve submission"><CheckCircle className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleReject(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer" title="Reject submission (reason required)"><XCircle className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                  {canEdit && r.status === 'Submitted' && r.requestType !== 'Verification' && r.requiresApproval === false && (
                    <>
                      <button onClick={() => handleComplete(r)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer" title="Mark Completed (reviewed)"><CheckCircle className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleReject(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer" title="Reject submission (reason required)"><XCircle className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                  {canEdit && !TERMINAL.includes(r.status) && r.requestType !== 'Verification' && (
                    <>
                      <button onClick={() => handleOverride(r)} className="px-2 py-1 rounded-lg hover:bg-amber-50 text-amber-600 text-[11px] font-bold cursor-pointer" title="Override (requires a recorded reason)">Override</button>
                      <button onClick={() => handleCancel(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer" title="Cancel (requires a recorded reason)"><XCircle className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {viewing && (
        <div className="fixed inset-0 z-[8000] flex items-center justify-center bg-black/40 p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-sm font-bold text-slate-800">{viewing.documentName}</h4>
              <button aria-label="Close" title="Close" onClick={() => setViewing(null)} className="p-1 rounded-lg hover:bg-slate-100 cursor-pointer"><X className="h-4 w-4" /></button>
            </div>
            <dl className="space-y-2 text-xs text-slate-600">
              <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Status</dt><dd>{viewing.status}{viewing.completionVia ? ` (via ${viewing.completionVia})` : ''}</dd></div>
              <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Type</dt><dd>{viewing.requestType} · response format {viewing.responseType} · priority {viewing.priority}{viewing.mandatory ? ' · Mandatory' : ''}</dd></div>
              <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Recipient</dt><dd>{viewing.recipientEmail || '—'}</dd></div>
              <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Description</dt><dd className="whitespace-pre-wrap">{viewing.description}</dd></div>
              {viewing.instructions && <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Instructions</dt><dd className="whitespace-pre-wrap">{viewing.instructions}</dd></div>}
              {(viewing.requiredDocs || []).length > 0 && (
                <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Required</dt><dd>{(viewing.requiredDocs || []).map((d: any) => (typeof d === 'string' ? d : d?.name || '')).filter(Boolean).join(', ')}</dd></div>
              )}
              <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Notifications</dt><dd>On submit: {viewing.notifyAdminOnSubmit ? 'on' : 'off'} · On completion: {viewing.notifyAdminOnCompletion ? 'on' : 'off'}</dd></div>
              {(viewing.templateName || viewing.requiresApproval !== undefined || viewing.blocksWorkspaceAccess !== undefined) && (
                <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Settings</dt><dd>
                  {viewing.templateName ? `From template: ${viewing.templateName} · ` : ''}
                  Blocks access: {(viewing.blocksWorkspaceAccess ?? viewing.mandatory) ? 'yes' : 'no'} · Requires approval: {viewing.requiresApproval !== false ? 'yes' : 'no'}
                </dd></div>
              )}
              {viewing.reviewedAt && (
                <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Review</dt><dd>
                  {viewing.reviewDecision} by {viewing.reviewedByName || viewing.reviewedBy || 'an administrator'} · {fmtDate(viewing.reviewedAt)}
                  {viewing.rejectionReason ? ` — Reason: ${viewing.rejectionReason}` : ''}
                </dd></div>
              )}
              {(viewing.submissions || []).length > 0 && (
                <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Submissions</dt><dd>
                  {(viewing.submissions || []).map((v) => `v${v.version} · ${fmtDate(v.submittedAt)} · ${v.submittedBy === 'public_token' ? 'emailed link' : v.submittedBy}`).join(' · ')}
                </dd></div>
              )}
              {(viewing.selectedValues || []).length > 0 ? (
                <div>
                  <dt className="font-bold text-slate-400 uppercase text-[10px]">Selected Choices</dt>
                  <dd className="flex flex-wrap gap-1 mt-0.5">
                    {(viewing.selectedValues || []).map((v, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[11px] font-semibold">{v}</span>
                    ))}
                  </dd>
                </div>
              ) : viewing.responseValue ? (
                <div><dt className="font-bold text-slate-400 uppercase text-[10px]">{viewing.requestType === 'Form' ? 'Form Answers' : 'Submitted Response'}</dt><dd className="whitespace-pre-wrap">{viewing.requestType === 'Form' ? renderFormattedResponseValue(viewing.responseValue, viewing.formFields as any) : viewing.responseValue}</dd></div>
              ) : null}
              {(viewing.responseFiles || []).length > 0 && (
                <div>
                  <dt className="font-bold text-slate-400 uppercase text-[10px]">Submitted Files</dt>
                  <dd>{(viewing.responseFiles || []).map((f, i) => (
                    <span key={i}><a className="text-indigo-600 underline" href={f.url} target="_blank" rel="noreferrer">{f.name}</a>{i < (viewing.responseFiles || []).length - 1 ? ', ' : ''}</span>
                  ))}</dd>
                </div>
              )}
              {viewing.overrideReason && <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Override Reason</dt><dd>{viewing.overrideReason}</dd></div>}
              {viewing.cancelReason && <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Cancel Reason</dt><dd>{viewing.cancelReason}</dd></div>}
              {viewing.requestType === 'Verification' && (
                <div><dt className="font-bold text-slate-400 uppercase text-[10px]">Didit Verification</dt><dd>Status: {viewing.verificationStatus || '—'}{viewing.diditSessionId ? ` · Session ${viewing.diditSessionId}` : ''}{viewing.verificationIssue ? ` · ${viewing.verificationIssue}` : ''}</dd></div>
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
